import { Box, SxProps, useMediaQuery, useTheme } from "@mui/material";
import { Typography } from "@powerdiary/components-library/lib";
import {
    clearBookingState,
    selectBooking,
    selectBookingType,
    setServiceForClass,
    useClientPortalDispatch,
    useClientPortalSelector,
} from "../../../../redux";
import { BookingStatus } from "../../bookingSlice.types";
import { useBookingConfirmation } from "../../hooks/useBookingConfirmation";
import { useClientPortalConfigContext } from "context/ClientPortalConfig.ctx";
import { useEffect, useMemo } from "react";
import LoadingOverlay from "../../../Shared/LoadingOverlay";
import ConfirmationWithPayment from "./ConfirmationWithPayment";
import ConfirmationNoPayment from "./ConfirmationNoPayment";
import { getPaymentFlags, getSelectedService } from "./Confirmation.helpers";
import { useDefaultGroupService } from "./useDefaultGroupService";
import { useRelatedClients } from "./useRelatedClients";
import { useClientDiscount } from "./useClientDiscount";
import { usePaymentAlreadyReceived } from "./usePaymentAlreadyReceived";
import { BookingType } from "types/appointmentBooking";

export default function Confirmation() {
    const { session, availability, service, provider } = useClientPortalSelector(selectBooking);
    const bookingType = useClientPortalSelector(selectBookingType);
    const { confirmBooking, isLoading, isSuccess: bookedSuccessfully } = useBookingConfirmation();
    const dispatch = useClientPortalDispatch();

    const {
        clientPortalConfig: { appointmentsPaymentsMode, groupPaymentsMode, userDetails, defaultStripeAccount },
    } = useClientPortalConfigContext();

    const stripeConnected = !!defaultStripeAccount || !!provider.data?.providerStripeAccount;

    const { relatedClients, isLoading: isLoadingRelatedClients } = useRelatedClients();

    const shouldShowContactsDropdown = useMemo(() => {
        if (!relatedClients) {
            return false;
        }

        return relatedClients.length > 1 || !relatedClients.some((client) => client.id === userDetails?.clientId);
    }, [relatedClients, userDetails]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const { defaultGroupService } = useDefaultGroupService({
        appointmentId: availability?.data?.timeEntry?.appointmentId || "",
        pause: bookingType === BookingType.CLIENT && !availability?.data?.timeEntry?.appointmentId,
    });

    useEffect(() => {
        const selectedService = getSelectedService(defaultGroupService, service?.data, bookingType);
        if (service?.data?.serviceId !== selectedService?.serviceId) {
            dispatch(setServiceForClass(selectedService));
        }
    }, [bookingType, defaultGroupService, service, dispatch]);

    const isBookingCompleted = session.bookingStatus === BookingStatus.BOOKED;
    const completedData = session.completedBookingData;
    const isBookingSuccessful = isBookingCompleted && completedData !== null;

    const { applyDiscountToAmount } = useClientDiscount();
    const { paymentAlreadyReceived, isChecking: isCheckingPaymentAlreadyReceived } = usePaymentAlreadyReceived();

    const discountedAmount = useMemo(
        () => applyDiscountToAmount(service?.data?.amount ?? 0),
        [service?.data?.amount, applyDiscountToAmount]
    );

    const { paymentEnabled, paymentRequired, paymentOptional } = useMemo(
        () =>
            getPaymentFlags(
                bookingType,
                appointmentsPaymentsMode,
                groupPaymentsMode,
                service?.data,
                stripeConnected,
                discountedAmount,
                paymentAlreadyReceived
            ),
        [
            bookingType,
            appointmentsPaymentsMode,
            groupPaymentsMode,
            service,
            stripeConnected,
            discountedAmount,
            paymentAlreadyReceived,
        ]
    );

    useEffect(() => {
        if (isBookingSuccessful) {
            // Capture the booking type at the time the booking completed
            // This ensures we clear the correct booking type even if the user
            // navigates to a different booking flow before this cleanup runs
            const completedBookingType = bookingType;
            return () => {
                dispatch(clearBookingState(completedBookingType));
            };
        }
    }, [dispatch, isBookingSuccessful, bookingType]);

    const wrapperSx: SxProps = useMemo(
        () => ({
            padding: isMobile ? "16px" : "16px 0",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
        }),
        [isMobile]
    );

    return (
        <Box sx={wrapperSx}>
            {(isLoading || isLoadingRelatedClients || isCheckingPaymentAlreadyReceived) && <LoadingOverlay />}
            <Typography variant="heading4">
                {bookedSuccessfully ? "Booking Details" : "Take a moment to review your booking details"}
            </Typography>
            {paymentAlreadyReceived && !bookedSuccessfully && (
                <Typography variant="paragraphSmallRegular" data-test-id="text_payment_already_received">
                    Your earlier payment for this booking is still on file and covers it, so no further payment is
                    needed.
                </Typography>
            )}
            {!isCheckingPaymentAlreadyReceived && paymentEnabled && (
                <ConfirmationWithPayment
                    paymentOptional={paymentOptional}
                    paymentRequired={paymentRequired}
                    confirmBooking={confirmBooking}
                    bookedSuccessfully={bookedSuccessfully}
                    shouldShowContactsDropdown={shouldShowContactsDropdown}
                    relatedClients={relatedClients || []}
                />
            )}
            {!isCheckingPaymentAlreadyReceived && !paymentEnabled && (
                <ConfirmationNoPayment
                    confirmBooking={confirmBooking}
                    isLoading={isLoading}
                    bookedSuccessfully={bookedSuccessfully}
                    shouldShowContactsDropdown={shouldShowContactsDropdown}
                    relatedClients={relatedClients || []}
                />
            )}
        </Box>
    );
}
